import { useNavigation } from '@app/hooks/useNavigation';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import SideMenuList from '@app/routes/Dashboard/SideMenuList.tsx';
import LangSelect from '@components/block/Header/LangSelect';
import { NotificationsMenu } from '@components/block/Header/NotificationsMenu';
import AccountDrawerMenu from '@components/ui/AccountMenu/AccountDrawerMenu.tsx';
import AccountMenu from '@components/ui/AccountMenu/AccountMenu';
import { ManageSubscriptionModal } from '@components/ui/AccountMenu/ManageSubscriptionModal';
import { LATEST_CHANGELOG_VERSION } from '@components/ui/AccountMenu/ChangelogSheet';
import TenantMenu from '@components/ui/TenantMenu/TenantMenu';
import { useUserLogoutMutation } from '@dieterApi/user/useUserLogoutMutation';
import { UserRole } from '@dieterApi/user/useUserQuery';
import { usePendingTenantRegistrationCountQuery } from '@dieterApi/tenantRegistration/usePendingTenantRegistrationCountQuery';
import { ArrowCircleUp, ConfirmationNumber, Stars, WorkspacePremium } from '@mui/icons-material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import MenuIcon from '@mui/icons-material/Menu';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import cx from 'classnames';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { animated, useSpring } from 'react-spring';
import './header.sass';
import { Logo } from './Logo';

interface HeaderProps {
  v2Mode?: boolean;
  sidebarTriggerComponent?: React.ReactNode;
}

export function Header({ v2Mode = false, sidebarTriggerComponent = null }: HeaderProps) {
  if (v2Mode && sidebarTriggerComponent === null) {
    console.warn('Header: a sidebarTriggerComponent must be passed in v2Mode.');
  }

  const { user, userLoading, activeCompany, whitelabelLoading, whitelabelConfig, topics } = useUserWithCompany();
  const history = useHistory();
  const { t } = useTranslation();

  // Principal admins of a MultiTenancy/DpoManaged company can review incoming
  // tenant registration requests. The badge shows the number of open requests.
  const isPrincipalAdmin =
    user?.role === UserRole.Admin &&
    (activeCompany?.isPrincipal ?? false) &&
    ((user?.hasFeature('MultiTenancy') ?? false) || (user?.hasFeature('DpoManaged') ?? false));
  const { data: pendingRegistrationsData } = usePendingTenantRegistrationCountQuery({ skip: !isPrincipalAdmin });
  const pendingRegistrations = pendingRegistrationsData?.pendingTenantRegistrationCount ?? 0;

  // Only display the header once whitelabel config is loaded to prevent color flash
  const [logout] = useUserLogoutMutation();

  const { navigation, setNavigation } = useNavigation();
  const { itemListOpen, isMobile } = navigation;
  const hasUnseenChangelog = navigation.changelogLastSeenVersion !== LATEST_CHANGELOG_VERSION;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [anchorEl2, setAnchorEl2] = useState<HTMLButtonElement | null>(null);
  const [openManageSubscriptionModal, setOpenManageSubscriptionModal] = useState(false);
  const [promoCodeId, setPromoCodeId] = React.useState<string | undefined>(undefined);

  const [animateCompanyName, setAnimateCompanyName] = useState(false);

  const isExternal = navigation.scope === 'quest_external';
  const isOnboardingScope = navigation.scope === 'onboarding';

  const companyNameNormalColor = v2Mode ? '#000' : '#fff';
  const companyNameFlashColor = v2Mode ? '#fff' : '#000';
  const animate = useSpring({
    to: {
      color: animateCompanyName ? companyNameFlashColor : companyNameNormalColor,
    },
    onRest: () => setAnimateCompanyName(false),
  });

  useEffect(() => {
    // on logout close drawers
    if (!user) {
      setAnchorEl(null);
      setAnchorEl2(null);
    }
  }, [user]);

  useEffect(() => {
    if (user?.company.name) {
      setAnimateCompanyName(true);
    }
  }, [user?.company.name]);

  // Handle promo code from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const promoCode = urlParams.get('promocodeid');

    if (promoCode) {
      setPromoCodeId(promoCode);
      setOpenManageSubscriptionModal(true);
    }
  }, []);

  const handleAccountClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMultiTenancyClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl2(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAnchorEl2(null);
  };

  const userName = user?.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user?.email || 'N N';
  const handleCloseSideMenu = () => setNavigation((nav) => void (nav.itemListOpen = !nav.itemListOpen));

  if (whitelabelLoading) {
    // Reserve space but don't render content to prevent flash
    return (
      <div
        className={cx(v2Mode ? 'min-h-[48px] md:min-h-[64px]' : 'min-h-[80px]', 'w-full')}
        style={{ visibility: 'hidden' }}
      />
    );
  }

  return (
    <>
      <header
        className={cx(v2Mode ? 'min-h-[48px] md:min-h-[64px]' : 'min-h-[80px]', 'items-center flex', {
          dtHeader: !v2Mode,
          'dtHeader--light': v2Mode,
          'hide-header': navigation.isPrintView,
        })}
      >
        {/* DESKTOP HEADER */}
        <div className={cx('w-full hidden md:block')}>
          <div className={cx('flex gap-5 justify-between mx-auto px-9', v2Mode ? 'w-full' : '2xl:container')}>
            <div
              className={cx(
                'dtHeader__brand flex-none md:flex md:gap-5 md:flex-2 md:relative md:translate-x-0 md:left-0 absolute -translate-x-1/2 left-1/2',
                !isExternal && !isOnboardingScope && 'cursor-pointer'
              )}
            >
              <Logo />
            </div>
            <div className="flex-7 hidden md:block" />
            <div className="dtHeader__user flex gap-5 md:gap-8 flex-1 items-center justify-end">
              {/* LANGUAGE BUTTON */}
              <LangSelect />
              {/* NOTIFICATION BUTTON */}
              {!user?.isAnonymous && <NotificationsMenu />}
              {user && activeCompany && (
                <>
                  {activeCompany.name && (
                    // Display active Tenant
                    <div className="flex items-center gap-2">
                      <animated.div style={animate} className="hidden md:block">
                        {activeCompany.name}
                      </animated.div>
                      {user.companies.length > 1 && (
                        <button
                          onClick={handleMultiTenancyClick}
                          className="text-xs border border-gray-300 rounded-sm hover:bg-primary-100 hover:text-primary-root transition-colors"
                        >
                          <KeyboardArrowDownIcon fontSize="inherit" />
                        </button>
                      )}
                    </div>
                  )}
                  {/* // SUBSCRIPTION BADGE */}
                  {!whitelabelConfig &&
                    (user.subscription ? (
                      <div className="flex flex-col w-full items-center gap-3">
                        <Tooltip
                          enterTouchDelay={0}
                          leaveTouchDelay={5000}
                          title={
                            user.subscription === 'Ticket' ? (
                              <div className="flex flex-col gap-1">
                                <Trans t={t} i18nKey="layout.global.header.subscription_badge">
                                  <strong className="font-bold">Freigeschaltene Bereiche:</strong>
                                  <ul
                                    // @ts-expect-error
                                    i18nIsDynamicList
                                  >
                                    {activeCompany?.topicsActivated.map((topicId, index) => (
                                      <li key={index}>{topics.find((t) => t.id === topicId)?.title}</li>
                                    ))}
                                  </ul>
                                </Trans>
                              </div>
                            ) : undefined
                          }
                          placement="bottom"
                        >
                          <button
                            className="dtHeader__subscriptiontag hidden md:flex flex-col items-center group relative  hover:outline-white hover:outline-2 min-w-[100px]"
                            onClick={() => history.push('/pricing')}
                            data-testid="button-subscription-tag"
                          >
                            <div className="group-hover:text-black flex items-center gap-[2px]">
                              <span>{activeCompany?.subscription}</span>
                              <span>{activeCompany?.isPlus && '+'}</span>
                              <span>{user.isTrialing && 'Test'}</span>
                              <span>{activeCompany?.isDpo && t('layout.global.header.dpo_badge')}</span>
                              {activeCompany?.isAgency && t('layout.global.header.agency_badge')}
                              {activeCompany?.subscription === 'Ticket' && <ConfirmationNumber fontSize="small" />}
                              {activeCompany?.subscription === 'Premium' && <WorkspacePremium fontSize="small" />}
                              {activeCompany?.subscription === 'Basic' && <Stars fontSize="small" />}
                            </div>
                            <div className="hidden group-hover:flex absolute left-1/2 -translate-x-1/2  items-center gap-1">
                              <span>Upgrade</span>
                              <ArrowCircleUp fontSize="small" />
                            </div>
                          </button>
                        </Tooltip>
                        {/* <button className="text-sm underline hover:text-primary-root hover:bg-white transition-all duration-300 px-2 rounded-lg">
                        Upgrade
                      </button> */}
                      </div>
                    ) : (
                      /* // Pricing Link */
                      ['dashboard'].includes(navigation.scope || '') && (
                        <button
                          className="border-white border rounded-md px-2 py-1 hover:bg-white hover:text-primary-root text-sm transition duration-300 ease-in-out"
                          onClick={() => history.push('/pricing')}
                          data-testid="button-pricing"
                        >
                          <div className="flex gap-1 items-center">
                            {/* <OpenInNewIcon fontSize="inherit" /> */}
                            {t('layout.global.header.pricing_link', 'Preise')}
                          </div>
                        </button>
                      )
                    ))}

                  {/* // ADMIN BUTTON */}
                  {user.isAdmin && (
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      title={t('layout.global.header.admin_link', 'Administration')}
                    >
                      <button
                        className="dtHeader__button self-center hidden md:block"
                        onClick={() => history.push('/admin')}
                        data-testid="admin-button"
                      >
                        <AdminPanelSettingsOutlinedIcon />
                      </button>
                    </Tooltip>
                  )}

                  {/* // TENANT REGISTRATIONS BUTTON */}
                  {isPrincipalAdmin && (
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      title={t('layout.global.header.tenant_registrations_link', 'Registrierungsanfragen')}
                    >
                      <button
                        className="dtHeader__button self-center hidden md:block"
                        onClick={() => history.push('/tenant-registrations')}
                        data-testid="tenant-registrations-button"
                      >
                        <Badge badgeContent={pendingRegistrations} color="error">
                          <HowToRegOutlinedIcon />
                        </Badge>
                      </button>
                    </Tooltip>
                  )}

                  {/* ACCOUNT BUTTON */}
                </>
              )}
              <Tooltip
                enterTouchDelay={0}
                leaveTouchDelay={5000}
                title={
                  !user || user?.isAnonymous ? 'Info' : t('layout.global.header.account_dropdown_button', 'Account')
                }
                placement="bottom"
              >
                <button className="dtHeader__button" onClick={handleAccountClick} data-testid="account-button">
                  {!user || user?.isAnonymous ? (
                    <InfoIcon />
                  ) : (
                    <span className="relative inline-flex">
                      <Avatar {...stringAvatar(userName)} />
                      {hasUnseenChangelog && (
                        <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                      )}
                    </span>
                  )}
                </button>
              </Tooltip>
              {user?.isAnonymous && (
                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={t('common.action.login', 'Einloggen')}>
                  <button className="dtHeader__button self-center" onClick={() => logout()}>
                    <LoginRoundedIcon />
                    {/* {<Icon type={user.isAnonymous ? 'login-36' : 'logout-36'} scale={0.9} size={24} /> } */}
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {/* MOBILE HEADER */}
        <div className={cx('flex items-center px-3 w-full md:hidden', { hidden: !isMobile })}>
          {/* Left: Logo */}
          <div className={cx('dtHeader__brand flex-none pl-4', !isExternal && !isOnboardingScope && 'cursor-pointer')}>
            <Logo />
          </div>

          {/* Center: Flexible space */}
          <div className="flex-1" />

          {/* Right: Sidebar trigger/LangSelect and Account buttons */}
          <div className="flex items-center gap-4">
            {v2Mode ? (
              user && !user?.isAnonymous ? (
                sidebarTriggerComponent
              ) : (
                <LangSelect />
              )
            ) : user && !user?.isAnonymous ? (
              <IconButton
                style={{ color: 'white' }}
                aria-label="menu-toggle"
                size="small"
                data-testid="nav-toggle-mobile"
                onClick={() => setNavigation((nav) => void (nav.itemListOpen = !nav.itemListOpen))}
              >
                <MenuIcon fontSize="large" />
              </IconButton>
            ) : (
              <LangSelect />
            )}
            <Tooltip
              title={!user || user?.isAnonymous ? 'Info' : t('layout.global.header.account_dropdown_button', 'Account')}
              placement="bottom"
            >
              <button className="dtHeader__button" onClick={handleAccountClick} data-testid="account-button-mobile">
                {!user || user?.isAnonymous ? (
                  <InfoIcon />
                ) : (
                  <span className="relative inline-flex">
                    <Avatar {...stringAvatar(userName)} />
                    {hasUnseenChangelog && (
                      <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                    )}
                  </span>
                )}
              </button>
            </Tooltip>
            {user?.isAnonymous && (
              <Tooltip title={t('common.action.login', 'Einloggen')}>
                <button className="dtHeader__button self-center" onClick={() => logout()}>
                  <LoginRoundedIcon />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </header>
      {isMobile ? (
        <>
          <AccountDrawerMenu
            open={!!anchorEl}
            onClose={handleClose}
            user={user}
            stringAvatar={stringAvatar(userName).children}
            onOpenManageSubscription={() => setOpenManageSubscriptionModal(true)}
          />
          <Drawer
            anchor="left"
            open={itemListOpen}
            onClose={handleCloseSideMenu}
            sx={{
              zIndex: 99999,
              width: '80vw',
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: '80vw',
                boxSizing: 'border-box',
              },
            }}
          >
            <div className="dtHeader px-2 flex items-center justify-between text-white">
              <div className="dtHeader__brand ml-4">
                <Logo clickable={false} />
              </div>
              <IconButton
                aria-label="close"
                color="inherit"
                onClick={handleCloseSideMenu}
                data-testid="nav-drawer-close"
              >
                <CloseIcon />
              </IconButton>
            </div>
            <SideMenuList />
          </Drawer>
        </>
      ) : (
        <AccountMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          user={user}
          onOpenManageSubscription={() => setOpenManageSubscriptionModal(true)}
        />
      )}
      <TenantMenu anchorEl={anchorEl2} handleClose={handleClose} user={user} />
      <ManageSubscriptionModal
        open={openManageSubscriptionModal}
        onClose={() => setOpenManageSubscriptionModal(false)}
        promoCodeId={promoCodeId}
      />
    </>
  );
}

function stringAvatar(name: string) {
  const splitString = name.includes('.') ? '.' : name.includes('-') ? '-' : name.includes('@') ? '@' : ' ';
  const nameSplit = name.toUpperCase().split(splitString);
  return {
    children: `${nameSplit[0][0]}`, //${nameSplit[1][0]}
  };
}
